import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Column,
  FlexBox,
  FormError,
  FormField,
  FormInputGroup,
  GridContainer,
  H2,
  Row,
  StandardForm,
  TextInput,
  TextInputFloatingLabel,
  TextInputWithFloatingLabel,
  Typography,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import EmailValidator from '../../../validators/EmailValidator';
import { AccountSignInPrompt } from '../../shared/AccountSignInPrompt';

/*
  Collect and validate order info required to fetch an anonymously placed order
*/

export const AnonymousOrderLookupFormDisplay = ({
  setEmail,
  setOrderNumber,
  orderNumber,
}) => {
  const [anonymousOrderDetailsMessages, findOrderFromOrderHistoryMessages] =
    useTranslations([
      'anonymousOrderDetailsMessages',
      'findOrderFromOrderHistoryMessages',
    ]);

  const [emailInput, setEmailInput] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  // "Touched" means the user has interacted with field and then navigated away (i.e. to another field or submitted the form)
  const [isEmailTouched, setIsEmailTouched] = useState(false);

  const [orderNumberInput, setOrderNumberInput] = useState(orderNumber);
  const [isOrderNumberValid, setIsOrderNumberValid] = useState(!!orderNumber);
  // "Touched" means the user has interacted with field and then navigated away (i.e. to another field or submitted the form)
  const [isOrderNumberTouched, setIsOrderNumberTouched] =
    useState(!!orderNumber);

  const [emailValidityMessageKey, setEmailValidityMessageKey] = useState(
    EmailValidator.getValidityMessageKey(emailInput)
  );
  const emailValidityMessage = emailValidityMessageKey
    ? anonymousOrderDetailsMessages[emailValidityMessageKey]
    : undefined;

  // Validates the given email, then persists its validity status and message accordingly
  // Returns true for a valid email, false for invalid
  const validateEmail = email => {
    const emailValidity = EmailValidator.isEmailValid(email);
    setIsEmailValid(emailValidity);
    setEmailValidityMessageKey(EmailValidator.getValidityMessageKey(email));
    return emailValidity;
  };

  // Validates the given order number, then persists its validity status
  // Returns true for a valid order number (non-empty), false for invalid (empty)
  const validateOrderNumber = orderNum => {
    const isEmpty = orderNum.length === 0;
    setIsOrderNumberValid(!isEmpty);
    return !isEmpty;
  };

  // On input of email, persist and validate it
  const onEmailInput = event => {
    // `trim()` prevents the user from typing any whitespace in the input field,
    // and removes leading or trailing white space that is inputted via copy/paste
    const input = event.target.value.trim();
    setEmailInput(input);
    validateEmail(input);
  };

  // On input of order number, persist and validate it
  const onOrderNumberInput = event => {
    // `trim()` prevents the user from typing any whitespace in the input field,
    // and removes leading or trailing white space that is inputted via copy/paste
    const input = event.target.value.trim();
    setOrderNumberInput(input);
    validateOrderNumber(input);
  };

  // Update the state in the parent component, as long as both inputs are valid
  const setFormInput = () => {
    // If both inputs are valid, persist them
    if (isEmailValid && isOrderNumberValid) {
      setEmail(emailInput);
      setOrderNumber(orderNumberInput);
    }
    // If either input is invalid, flag the field as "touched" so validations can show
    else {
      setIsEmailTouched(true);
      setIsOrderNumberTouched(true);
    }
  };

  // Attempt to submit the form from either text input box using the Enter key
  const submitForm = event => {
    if (event.key === 'Enter') {
      setFormInput();
    }
  };

  return (
    <GridContainer dividers mb={7} py={6}>
      <Row mt={7} mb={7}>
        <Column span={12}>
          <H2 textAlign="center">{anonymousOrderDetailsMessages.formTitle}</H2>
        </Column>
      </Row>
      <Row>
        <Column span={6}>
          <AccountSignInPrompt />
        </Column>
        <Column span={6} mb={7}>
          <FlexBox flexDirection="column" alignItems="center" pt={5} gap={5}>
            <Typography fontSize="large" fontWeight="bold">
              {anonymousOrderDetailsMessages.findGuestOrder}
            </Typography>
            <Typography>
              {anonymousOrderDetailsMessages.enterEmailToFindOrder}
            </Typography>
            <StandardForm style={{ width: '60%' }}>
              <FormField>
                <FormInputGroup>
                  <TextInputWithFloatingLabel>
                    <TextInput
                      onBlur={() => setIsOrderNumberTouched(true)}
                      onInput={onOrderNumberInput}
                      onKeyDown={submitForm}
                      skin={
                        isOrderNumberTouched && !isOrderNumberValid
                          ? 'error'
                          : 'standard'
                      }
                      value={orderNumberInput}
                      aria-required="true"
                      id="anon-order-number-input"
                      data-testid="order-details-order-number"
                    />
                    <TextInputFloatingLabel htmlFor="anon-order-number-input">
                      {anonymousOrderDetailsMessages.enterYourOrderNumber}
                    </TextInputFloatingLabel>
                  </TextInputWithFloatingLabel>
                  {isOrderNumberTouched && !isOrderNumberValid && (
                    <FormError>
                      {
                        findOrderFromOrderHistoryMessages.orderNumberRequiredError
                      }
                    </FormError>
                  )}
                </FormInputGroup>
              </FormField>
              <FormField>
                <FormInputGroup>
                  <TextInputWithFloatingLabel>
                    <TextInput
                      onBlur={() => setIsEmailTouched(true)}
                      onInput={onEmailInput}
                      onKeyDown={submitForm}
                      skin={
                        isEmailTouched && !isEmailValid ? 'error' : 'standard'
                      }
                      value={emailInput}
                      aria-required="true"
                      id="anon-order-email-input"
                      data-testid="order-details-email"
                    />
                    <TextInputFloatingLabel htmlFor="anon-order-email-input">
                      {anonymousOrderDetailsMessages.enterYourEmailAddress}
                    </TextInputFloatingLabel>
                  </TextInputWithFloatingLabel>
                  {isEmailTouched && !isEmailValid && (
                    <FormError>{emailValidityMessage}</FormError>
                  )}
                </FormInputGroup>
              </FormField>

              <Button
                skin="secondary"
                width="full-width"
                onClick={setFormInput}
                data-testid="order-details-submit-button"
              >
                {anonymousOrderDetailsMessages.continue}
              </Button>
            </StandardForm>
          </FlexBox>
        </Column>
      </Row>
    </GridContainer>
  );
};

AnonymousOrderLookupFormDisplay.propTypes = {
  setEmail: PropTypes.func.isRequired,
  setOrderNumber: PropTypes.func.isRequired,
  orderNumber: PropTypes.string,
};
